import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export default function AdminScraper() {
  const { getToken } = useAuth();
  const [apiKey, setApiKey] = useState<string>('');
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Fetch the scraper API key
    async function fetchApiKey() {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/admin/scraper-key`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setApiKey(data.apiKey);
        }
      } catch (error) {
        console.error('Failed to fetch API key:', error);
      }
    }

    // Check server status
    async function checkServer() {
      try {
        const response = await fetch(`${API_URL.replace('/api', '')}/health`);
        if (response.ok) {
          setServerStatus('online');
        } else {
          setServerStatus('offline');
        }
      } catch {
        setServerStatus('offline');
      }
    }

    fetchApiKey();
    checkServer();
  }, [getToken]);

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-cream-100">Scraper Scripts</h1>
        <p className="text-cream-200/70 mt-1">Install Tampermonkey scripts to scrape job posts from social platforms</p>
      </div>

      {/* Server Status */}
      <div className="bg-midnight-800/50 rounded-lg p-4 border border-cream-100/10">
        <div className="flex items-center justify-between">
          <span className="text-cream-200/70">Server Status</span>
          <span className={`flex items-center gap-2 ${
            serverStatus === 'online' ? 'text-green-400' :
            serverStatus === 'offline' ? 'text-red-400' : 'text-yellow-400'
          }`}>
            <span className={`w-2 h-2 rounded-full ${
              serverStatus === 'online' ? 'bg-green-400' :
              serverStatus === 'offline' ? 'bg-red-400' : 'bg-yellow-400'
            }`}></span>
            {serverStatus === 'checking' ? 'Checking...' :
             serverStatus === 'online' ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

      {/* API Key */}
      <div className="bg-midnight-800/50 rounded-lg p-4 border border-cream-100/10">
        <h3 className="text-sm font-medium text-cream-200/70 mb-2">Scraper API Key</h3>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-midnight-900 px-3 py-2 rounded text-sm text-cream-100 font-mono">
            {apiKey || 'Loading...'}
          </code>
          <button
            onClick={copyApiKey}
            className="px-3 py-2 bg-gold-500 text-midnight-900 rounded text-sm font-medium hover:bg-gold-400 transition-colors"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="text-xs text-cream-200/50 mt-2">
          You'll need this key when configuring the scraper script
        </p>
      </div>

      {/* LinkedIn Scraper */}
      <div className="bg-midnight-800/50 rounded-lg p-6 border border-cream-100/10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-display font-semibold text-cream-100">LinkedIn Job Scraper</h2>
            <p className="text-cream-200/70 text-sm mt-1">
              Scrapes job posts from LinkedIn and sends them to Guildhall for review
            </p>
          </div>
          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">LinkedIn</span>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-cream-100 mb-2">Features</h3>
            <ul className="text-sm text-cream-200/70 space-y-1">
              <li>• Automatic job scraping every 10 seconds</li>
              <li>• Extracts job title, company, location, description</li>
              <li>• Works on job search pages and individual job posts</li>
              <li>• Duplicate detection</li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-medium text-cream-100 mb-2">Installation</h3>
            <ol className="text-sm text-cream-200/70 space-y-2">
              <li>1. Install <a href="https://www.tampermonkey.net/" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:text-gold-300">Tampermonkey</a> browser extension</li>
              <li>2. Click the install button below</li>
              <li>3. Click "Install" in Tampermonkey</li>
              <li>4. Go to <a href="https://www.linkedin.com/jobs" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:text-gold-300">LinkedIn Jobs</a></li>
              <li>5. Click the Guildhall indicator and enter your API key</li>
              <li>6. Start browsing jobs!</li>
            </ol>
          </div>

          <a
            href={`${API_URL.replace('/api', '')}/linkedin-job-scraper.user.js`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500 text-midnight-900 rounded font-medium hover:bg-gold-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Install LinkedIn Scraper
          </a>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="bg-midnight-800/50 rounded-lg p-6 border border-cream-100/10">
        <h2 className="text-lg font-display font-semibold text-cream-100 mb-4">Quick Start</h2>
        <ol className="text-sm text-cream-200/70 space-y-2">
          <li>1. Make sure the Guildhall server is running</li>
          <li>2. Install the Tampermonkey script above</li>
          <li>3. Configure your API key in the script</li>
          <li>4. Add scraper targets in the <a href="/admin/targets" className="text-gold-400 hover:text-gold-300">Targets</a> page</li>
          <li>5. Browse LinkedIn to scrape jobs</li>
          <li>6. Review scraped posts in the <a href="/admin/posts" className="text-gold-400 hover:text-gold-300">Posts</a> page</li>
          <li>7. Convert good posts to jobs and publish them</li>
        </ol>
      </div>

      {/* Debugging */}
      <div className="bg-midnight-800/50 rounded-lg p-6 border border-cream-100/10">
        <h2 className="text-lg font-display font-semibold text-cream-100 mb-4">Debugging</h2>
        <p className="text-sm text-cream-200/70 mb-3">
          Open browser console (F12) on LinkedIn to see script activity:
        </p>
        <div className="space-y-2">
          <code className="block bg-midnight-900 px-3 py-2 rounded text-xs text-cream-100 font-mono">
            window.guildhall.scraper.getPosts() // View scraped posts
          </code>
          <code className="block bg-midnight-900 px-3 py-2 rounded text-xs text-cream-100 font-mono">
            window.guildhall.scraper.scrapeNow() // Manual scrape
          </code>
          <code className="block bg-midnight-900 px-3 py-2 rounded text-xs text-cream-100 font-mono">
            window.guildhall.scraper.sendNow() // Send to server
          </code>
        </div>
      </div>
    </div>
  );
}
